import { useRef, useEffect, useContext } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Card from 'react-bootstrap/Card'
import Spinner from 'react-bootstrap/Spinner'
import Page from 'layout/page'
import PageTitle from 'layout/page-title'
import { usePolicyDocuments, useExtractPolicyRules } from 'api/hooks/use-policy-documents'
import UserContext from 'context/user-context'
import PolicyUpload from './PolicyUpload'
import PolicyList from './PolicyList'
import PolicyItem from './PolicyItem'

const Policies = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const user = useContext(UserContext)
  const aiEnabled = user.municipality?.setting?.ai_search_enabled
  const hasInProgressDoc = (docs) =>
    docs?.some((d) => d.status === 'extracting' || d.matching_status === 'matching')

  const { data, isLoading } = usePolicyDocuments({
    options: {
      refetchInterval: (queryData) => {
        const docs = queryData?.data
        return hasInProgressDoc(docs) ? 5000 : false
      }
    }
  })
  const { mutateAsync: extractRules } = useExtractPolicyRules()

  const policies = data?.data || []
  const prevStatusesRef = useRef({})

  useEffect(() => {
    const prevStatuses = prevStatusesRef.current
    const nextStatuses = {}

    policies.forEach((doc) => {
      nextStatuses[doc.id] = doc.status
      const prev = prevStatuses[doc.id]

      if (prev === 'extracting' && doc.status === 'extracted') {
        toast.success(`Rules extracted for "${doc.name}".`)
      } else if (prev === 'extracting' && doc.status === 'failed') {
        toast.error(`Extraction failed for "${doc.name}": ${doc.error_message || 'Unknown error'}`)
      }
    })

    prevStatusesRef.current = nextStatuses
  }, [policies])

  const handleExtract = async (docId) => {
    try {
      await extractRules(docId)
      toast.info('Rule extraction started. This may take a minute...')
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to start rule extraction.')
    }
  }

  return (
    <Page title="Policies">
      <PageTitle />
      <Row>
        <Col>
          <Card>
            <Card.Body>
              {!id && (
                <>
                  <div className="text-primary fw-bold mb-3">Policy Documents</div>
                  <p className="text-muted small mb-4">
                    {aiEnabled
                      ? 'Create policy documents and define rules that apply to your organization. You can manually enter rules or upload a PDF for AI-powered extraction.'
                      : 'Create policy documents and define rules that apply to your organization.'}
                  </p>

                  <PolicyUpload onCreated={(newId) => newId && navigate(`/policies/${newId}`)} />

                  <hr className="my-4" />
                </>
              )}

              {isLoading ? (
                <div className="text-center py-3">
                  <Spinner animation="border" size="sm" />
                </div>
              ) : id ? (
                <PolicyItem
                  policyDocumentId={id}
                  onBack={() => navigate('/policies')}
                />
              ) : (
                <PolicyList
                  policies={policies}
                  onExtract={handleExtract}
                />
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Page>
  )
}

export default Policies
